import { collection, getDocs, query, where, onSnapshot, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../components/firebase-config.js';

/**
 * Récupère tous les médecins affiliés à une structure spécifique
 * Compatible avec l'ancien et le nouveau système d'affiliations
 */
export const getMedecinsAffiliesToStructure = async (structureId) => {
  try {
    const medecinsSnapshot = await getDocs(collection(db, 'medecins'));
    const medecinsAffilies = [];
    
    for (const medecinDoc of medecinsSnapshot.docs) {
      const medecinData = medecinDoc.data();
      const medecinId = medecinDoc.id;
      
      if (isMedecinAffiliatedToStructure(medecinData, structureId)) {
        medecinsAffilies.push({
          id: medecinId,
          ...medecinData
        });
      }
    }
    
    return medecinsAffilies;
  } catch (error) {
    console.error('Erreur lors de la récupération des médecins affiliés:', error);
    throw error;
  }
};

/**
 * Vérifie si un médecin est affilié à une structure spécifique
 * Compatible avec l'ancien et le nouveau système d'affiliations
 */
export const isMedecinAffiliatedToStructure = (medecinData, structureId) => {
  // Vérifier les affiliations multiples (nouveau système)
  const affiliatedStructures = medecinData.affiliatedStructures || [];
  const isAffiliatedViaNewSystem = affiliatedStructures.some(
    affiliation => affiliation.structureId === structureId && affiliation.status === 'active'
  );
  
  // Vérifier l'ancien système (compatibilité)
  const isAffiliatedViaOldSystem = medecinData.structureId === structureId;
  
  return isAffiliatedViaNewSystem || isAffiliatedViaOldSystem;
};

/**
 * Récupère toutes les structures affiliées à un médecin
 * Compatible avec l'ancien et le nouveau système d'affiliations
 */
export const getStructuresAffiliesToMedecin = async (medecinData) => {
  try {
    const structuresAffilies = [];
    
    // Nouveau système : affiliations multiples
    const affiliatedStructures = medecinData.affiliatedStructures || [];
    for (const affiliation of affiliatedStructures) {
      if (affiliation.status === 'active') {
        try {
          const structureDoc = await getDoc(doc(db, 'structures', affiliation.structureId));
          if (structureDoc.exists()) {
            structuresAffilies.push({
              id: affiliation.structureId,
              ...structureDoc.data(),
              affiliationDate: affiliation.affiliationDate
            });
          }
        } catch (error) {
          console.error(`Erreur lors de la récupération de la structure ${affiliation.structureId}:`, error);
        }
      }
    }
    
    // Fallback : ancien système
    if (structuresAffilies.length === 0 && medecinData.structureId) {
      try {
        const structureDoc = await getDoc(doc(db, 'structures', medecinData.structureId));
        if (structureDoc.exists()) {
          structuresAffilies.push({
            id: medecinData.structureId,
            ...structureDoc.data(),
            affiliationDate: medecinData.structureAffiliationDate
          });
        }
      } catch (error) {
        console.error(`Erreur lors de la récupération de la structure principale ${medecinData.structureId}:`, error);
      }
    }
    
    return structuresAffilies;
  } catch (error) {
    console.error('Erreur lors de la récupération des structures affiliées:', error);
    throw error;
  }
};

/**
 * Met en place un écouteur en temps réel pour les médecins affiliés à une structure
 */
export const setupMedecinsAffiliesToStructureListener = (structureId, callback) => {
  // Écouter tous les changements dans la collection medecins
  return onSnapshot(collection(db, 'medecins'), (snapshot) => {
    const medecinsAffilies = [];
    
    snapshot.docs.forEach((medecinDoc) => {
      const medecinData = medecinDoc.data();
      const medecinId = medecinDoc.id;
      
      if (isMedecinAffiliatedToStructure(medecinData, structureId)) {
        medecinsAffilies.push({
          id: medecinId,
          ...medecinData
        });
      }
    });
    
    callback(medecinsAffilies);
  });
};

/**
 * Met en place un écouteur en temps réel pour les structures affiliées à un médecin
 */
export const setupStructuresAffiliesToMedecinListener = (medecinId, callback) => {
  // Écouter les changements dans le document du médecin
  const medecinDocRef = doc(db, 'medecins', medecinId);
  
  return onSnapshot(medecinDocRef, async (docSnapshot) => {
    if (!docSnapshot.exists()) {
      callback([]);
      return;
    }
    
    const medecinData = docSnapshot.data();
    try {
      const structuresAffilies = await getStructuresAffiliesToMedecin(medecinData);
      callback(structuresAffilies);
    } catch (error) {
      console.error('Erreur lors de la récupération des structures affiliées:', error);
      callback([]);
    }
  });
};

/**
 * Ajoute une affiliation entre un médecin et une structure
 */
export const addMedecinStructureAffiliation = async (medecinId, structureId, structureNom) => {
  try {
    const medecinRef = doc(db, 'medecins', medecinId);
    const medecinDoc = await getDoc(medecinRef);
    
    if (!medecinDoc.exists()) {
      throw new Error('Médecin introuvable');
    }
    
    const medecinData = medecinDoc.data();
    const currentAffiliations = medecinData.affiliatedStructures || [];
    
    // Vérifier si l'affiliation existe déjà
    const existingAffiliation = currentAffiliations.find(
      aff => aff.structureId === structureId
    );
    
    if (existingAffiliation) {
      if (existingAffiliation.status === 'active') {
        throw new Error('Le médecin est déjà affilié à cette structure');
      } else {
        // Réactiver l'affiliation
        existingAffiliation.status = 'active';
        existingAffiliation.affiliationDate = Timestamp.now();
      }
    } else {
      // Ajouter une nouvelle affiliation
      currentAffiliations.push({
        structureId: structureId,
        structureNom: structureNom,
        affiliationDate: Timestamp.now(),
        status: 'active'
      });
    }
    
    // Mettre à jour le document
    await updateDoc(medecinRef, {
      affiliatedStructures: currentAffiliations
    });
    
    return true;
  } catch (error) {
    console.error('Erreur lors de l\'ajout de l\'affiliation:', error);
    throw error;
  }
};

/**
 * Supprime une affiliation entre un médecin et une structure
 */
export const removeMedecinStructureAffiliation = async (medecinId, structureId) => {
  try {
    const medecinRef = doc(db, 'medecins', medecinId);
    const medecinDoc = await getDoc(medecinRef);
    
    if (!medecinDoc.exists()) {
      throw new Error('Médecin introuvable');
    }
    
    const medecinData = medecinDoc.data();
    const currentAffiliations = medecinData.affiliatedStructures || [];
    
    // Retirer l'affiliation spécifique
    const updatedAffiliations = currentAffiliations.filter(
      aff => aff.structureId !== structureId
    );
    
    // Préparer les données de mise à jour
    const updateData = {
      affiliatedStructures: updatedAffiliations
    };
    
    // Gérer la compatibilité avec l'ancien système
    if (medecinData.structureId === structureId) {
      if (updatedAffiliations.length > 0) {
        // S'il reste d'autres structures, prendre la première comme structure principale
        const newPrimaryStructure = updatedAffiliations[0];
        updateData.structureId = newPrimaryStructure.structureId;
        updateData.structureNom = newPrimaryStructure.structureNom;
        updateData.structureAffiliationDate = newPrimaryStructure.affiliationDate;
        updateData.isAffiliated = true;
      } else {
        // Plus aucune structure, réinitialiser
        updateData.structureId = null;
        updateData.structureAffiliationDate = null;
        updateData.structureNom = '';
        updateData.isAffiliated = false;
      }
    }
    
    await updateDoc(medecinRef, updateData);
    return true;
    
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'affiliation:', error);
    throw error;
  }
}; 